/**
 * stageContent.ts — BizBuilder CJMS 5-Phase Customer Journey Framework
 *
 * Built from the 9 business logic documents:
 *   customer_journey_framework_outline.md
 *   customer_journey_phase1_awareness_and_acquisition_outline.md
 *   customer_journey_phase2_engagement_and_qualification_outline.md
 *   customer_journey_phase3_conversion_and_onboarding.md
 *   customer_journey_phase4_growth_and_ascension.md
 *   customer_journey_phase5_freedom_and_expansion_outline.md
 *   customer_journey_free_website_workflow.md
 *   customer_journey_3touch_rule_payment_follow-up.md
 *   customer_journey_discovery_call_precautions.md
 *
 * Phases:
 *   Phase 1: Awareness & Acquisition   (Stranger → Lead)
 *   Phase 2: Engagement & Qualification (Lead → Prospect)
 *   Phase 3: Conversion & Onboarding     (Prospect → Customer)
 *   Phase 4: Growth & Ascension          (Goal-to-10 → Goal-to-100 → Goal-to-1k)
 *   Phase 5: Freedom & Expansion          (Goal-to-10k → Goal-to-100k)
 */

export interface StageTask {
  label: string;
  detail: string;
}

export interface StageResource {
  title: string;
  description: string;
  type: 'guide' | 'template' | 'checklist' | 'video' | 'tool';
}

export interface StageContent {
  displayName: string;
  phase: number;
  description: string;
  steps: string[];
  activities: string[];
  /** Maps each step index to an array of activity indices that belong under it as sub-tasks */
  stepActivityMap: number[][];
  tasks: StageTask[];
  scripts: string[];
  resources: StageResource[];
}

// ─── Lead Stages (CRM_Leads data) ───────────────────────────────────────────

export const LEAD_STAGES: string[] = ['Lead Generation'];

export const LEAD_STAGE_TASKS: Record<string, string[]> = {
  'Lead Generation': [
    'Define your Freedom Number (monthly revenue target for ideal lifestyle)',
    'Establish your 30-Day Inception Goal (first 10 paid customers)',
    'Create your Hotlist 100 — warm network of potential contacts',
    'Perform 100 outreach actions today (Rule of 100)',
    'Send Irresistible Bribe script to new leads',
    'Log leads as "Leads Generated" in Marketing_Exec_Data',
    'Verify referral submissions and track progress',
    'Deliver free 1-pager website to leads who reach 5 referrals',
    'Transition to Hosting Handshake (R197 managed domain offer)',
  ],
};

export const LEAD_FALLBACK_SCRIPTS: Record<string, string[]> = {
  'Lead Generation': [
    // Irresistible Bribe — Rule of 100 outreach
    `Hey {{name}}, I'm running a mission to help 100 local businesses get online. I'm giving away a FREE 1-Pager Website with Free Lifetime Hosting (worth R3,997). The only catch: you refer 5 other business owners who also need this. Does your business need a professional upgrade?`,

    // Follow-up after interest
    `Hi {{name}}, great to hear you're interested in the free 1-pager! Here's the quick process: (1) I'll send you a referral form link; (2) you share it with 5 business owners; (3) once 5 accept, I build your site free. Ready to get started?`,

    // Referral form link
    `Hey {{name}}, here's your unique referral tracking link: [REFERRAL_LINK]. Your referrals can submit their details there, and you can track who's accepted. I'll notify you once you hit 5!`,

    // Congratulations — 5 referrals reached
    `🎉 Congrats {{name}}! All 5 referrals accepted the free website offer. I'm starting your 1-pager now. Expect it within 48 hours. While you wait, I'll also send you details about getting a professional custom domain.`,

    // Hosting Handshake transition
    `Hi {{name}}, your free 1-pager is live at [SUBDOMAIN]. To make it truly professional, I recommend a custom domain (R197/year .co.za or R397/year .com). I handle all the technical setup. Want me to secure yours before someone else takes it?`,

    // Template 1: Casual/Personal (Recommended)
    `Hi {{FriendName}}, I hope you're doing well! I'm reaching out because I know you're a busy business owner, and I wanted to share something that could save you money and help you grow. You can get a FREE 1-pager website (worth R3,997) with lifetime hosting (worth thousands more). The catch is: you need to refer 5 friends first to unlock it. Would you be open to learning more? If yes, I can send you the full details. No pressure at all! Best regards, {{ProspectName}}`,

    // Template 2: Professional/Formal
    `Dear {{FriendName}}, I hope this message finds you well. I'm writing to share an exclusive opportunity that I believe could benefit your business. Chrysalinks Group is offering FREE 1-pager websites (valued at R3,997) to business owners who participate in their referral program. The offer includes: • Professional custom design • Mobile responsive layout • SEO optimization • Lifetime hosting at no cost. If you're interested, I can share the full details with you. No obligation — just an opportunity worth exploring. Kind regards, {{ProspectName}}`,

    // Template 3: Urgency/Scarcity
    `Hi {{FriendName}}, I just learned about something I had to share with you immediately. Chrysalinks Group is giving away FREE 1-pager websites (worth R3,997) to the first 100 business owners who sign up. The offer includes lifetime hosting — completely FREE. The only requirement: refer 5 friends who also accept the offer. I'm already participating, and I thought of you because I know your business could benefit from a professional online presence. Want me to send you the details? This won't last long! Best, {{ProspectName}}`,

    // Template 4: Success Story/Social Proof
    `Hi {{FriendName}}, I just signed up for a FREE 1-pager website through Chrysalinks Group, and I'm blown away by the value. Here's what I got: • Professional custom design (normally R3,997) • Mobile responsive layout • SEO optimization • Lifetime hosting — FREE. The best part? It's completely free. The only catch is referring 5 friends, and then you get your own FREE website. I immediately thought of you. Would you like me to send you the details? No pressure — just sharing something I'm excited about! Best, {{ProspectName}}`,

    // Template 5: Problem-Solution
    `Hi {{FriendName}}, I know you've been looking for ways to grow your business online, and I found something that might help. Chrysalinks Group is offering FREE 1-pager websites to business owners. The website is professionally designed, mobile responsive, and includes lifetime hosting — all FREE. The only requirement: refer 5 friends who also accept the offer. Given your business goals, I think this could be a great fit. Would you like me to share the details? Best, {{ProspectName}}`,

    // Template 6: Direct/No-Nonsense
    `{{FriendName}}, Quick one: Chrysalinks Group is giving away free 1-pager websites to business owners. Worth R3,997 + lifetime hosting. Only catch: refer 5 friends first. Interested? I'll send the details. {{ProspectName}}`,

     // WhatsApp delivery instruction
     `[whatsapp:send] Template selected based on context: Personal=Template 1, Professional=Template 2, Time-sensitive=Template 3, Social-proof=Template 4, Problem-focused=Template 5, Direct=Template 6. Channel: WhatsApp (Primary), Email (Secondary), Portal (Self-service). Update CRM_Leads tab with acceptance status.`,
   ],
 };

 // ─── Referral Script Templates (Phase B Artifact 08) ─────────────────────────────

 export const REFERRAL_SCRIPT_TEMPLATES = {
   day_1: [
     {
       template: `Hi {friendName}, I wanted to share something special with you — I recently joined Chrysalinks and they're helping me build my online presence. They have a really cool referral program where you can get a free website if you refer friends!

Here's your personalized referral link: {link}

What's the process?
1. Click the link to create your account
2. Share it with 5 friends
3. Once they accept the offer, you unlock your own FREE website

No pressure at all — just wanted to let you know in case you're interested!

Best,
{userName}`,
       subject: "🎁 Quick favor from {userName}",
       timing: "Day 1 (immediate)",
       notes: "Use when user first logs in and wants to start sharing referrals"
     }
   ],
   day_3: [
     {
       template: `Hi {friendName},

I hope you're doing well!

Just wanted to quickly follow up on that referral link I shared — I'm looking to reach my 5-referral goal and would love your help.

The process is simple:
✅ Click the link to sign up
✅ Share it with your contacts
✅ Get your FREE website when 5 people accept

If you've already shared it, thanks so much — that means a lot to me!

If not, no worries at all — just wanted to remind you it's there if you ever need it.

Talk soon,
{userName}`,
       subject: "👋 Quick follow-up",
       timing: "Day 3",
       notes: "Use when user has made 1-2 referrals"
     }
   ],
   day_7: [
     {
       template: `Hi {friendName},

Just wanted to celebrate — you're making great progress with the referrals!

I'm now at {referralCount}/5 referrals, and every accepted one brings me closer to my FREE website. You've already helped me get {acceptedCount} referrals, which is amazing!

The link is still active: {link}

Once we hit 5 total, we both unlock our free websites. Let me know if you need any help sharing it further.

Keep crushing it,
{userName}`,
       subject: "🎉 Great progress!",
       timing: "Day 7",
       notes: "Use when user has made 3-4 referrals"
     }
   ],
   day_10: [
     {
       template: `Hi {friendName},

Quick favor — I'm one referral away from securing my free website! 🎉

Could you help me out by sharing the link with 1-2 more people? Even just posting it on social media or sharing it in a group chat would help.

Link: {link}

I'd really appreciate your help — it would mean so much to me!

Thanks in advance,
{userName}`,
       subject: "🎯 One referral away!",
       timing: "Day 10",
       notes: "Use when user has made 4 referrals"
     }
   ],
   day_12: [
     {
       template: `Hi {friendName},

🎉 **MASSIVE NEWS!** I just hit 5 accepted referrals!

That means I've officially unlocked my FREE website from Chrysalinks! You helped me get {referralCount} of those referrals — your support means the world to me.

The journey is awesome, and I've got a kickstarter to complete my website questionnaire. Once that's done, AI will generate my design in about 10 minutes.

If you've shared the link with anyone, thank you SO much! If not, no worries at all — you've already been a huge help.

Let's keep the momentum going!
{userName}`,
       subject: "🎉 I hit 5 referrals!",
       timing: "Day 12",
       notes: "Use when user has made 5+ referrals"
     }
   ],
   day_14: [
     {
       template: `Hi {friendName},

🎉 Can't believe it — you've accepted the referral!

Thanks a million for supporting me. This brings me one step closer to my FREE website.

Here's your referral link (share it with 4 more friends to unlock yours): {link}

We're both on the same journey now — let's get those referrals rolling!

Talk soon,
{userName}`,
       subject: "🎉 Referral accepted!",
       timing: "Day 14+",
       notes: "Use when a friend accepts the referral"
     }
   ]
 };

// ─── Customer Stages (Customers data) ────────────────────────────────────────

export const CUSTOMER_STAGES: string[] = [
  'Lead Conversion',     // Phase 2+3: Engagement & Qualification → Conversion
  'Fulfillment',          // Phase 3: Onboarding, Inception Sprint (Goal-to-10)
  'Retention',            // Phase 4: Launch (Goal-to-100), Known Brand
  'Growth',               // Phase 4: Goal-to-1k — Client Monetization
  'Advocacy',             // Phase 4+5: Brand → Freedom, Referral Machine
];

export const STAGE_TASKS: Record<string, string[]> = {
  'Lead Conversion': [
    'Run BANT qualification (Budget, Authority, Need, Timeline)',
    'Send Hosting Handshake offer (R197 managed domain)',
    'Conduct 15-min Discovery Call with SPS',
    'Schedule Solution Presentation Call Appointment after diagnosis',
    'Present 4 P\'s demo (Picture, Promise, Proof, Push)',
    'Send 1-Page Proposal with 5 required elements',
    'Execute 3-Touch Rule payment follow-up sequence',
    'Move non-qualified leads to Nurture List (30-90 day return path)',
  ],
  'Fulfillment': [
    'Trigger Provisioning Engine — clone client database',
    'Send Golden Handshake Welcome Pack with Activation Tasks',
    'Task 1: Complete Lesson 1 (Platform Orientation)',
    'Task 2: Set your Freedom Number in Goals section',
    'Task 3: Start your Hotlist 100 in Marketing',
    'Drip Stage 1 (Inception) playbook lessons on task completion',
    'Conduct weekly coaching consultation (coaching clients)',
    'Monitor Goal-to-10 30-day sprint progress',
    'Use 15:5:3 Success Math — 15 outreaches → 5 conversations → 3 sales',
    'Apply 10-a-Day Mantra — have 10 sales conversations daily',
    'Verify 10 paying customers in OF_Exec_Data for graduation',
  ],
  'Retention': [
    'Run 7-Day Health Check satisfaction survey',
    'Execute Golden Moment: testimonial + referral ask for 4+ scores',
    'Feed referrals back into pipeline (Referral Machine)',
    'Present Goal-to-100 Launch upsell',
    'Monthly account review — show ROI and outcomes',
    'Track engagement metrics and intervene below threshold',
  ],
  'Growth': [
    'Develop upsell/cross-sell strategy for CLV maximization',
    'Apply leverage: Labor (hire team), Capital (ads), Content, Code',
    'Master the "Power of One" — one persona, one channel',
    'Document SOPs for repeatable delivery process',
    'Segment market for higher efficiency targeting',
    'Target 10/200 Leadership Ratio — you lead, team executes',
  ],
  'Advocacy': [
    'Stage 4 — Full Operation (Goal-to-10k): Hire management team to run repeatable model',
    'Achieve 10/200 Leadership Ratio — 10h strategic guidance vs 200h team execution',
    'Focus on intensive market segmentation and professionalized departments',
    'Stage 5 — Corporate (Goal-to-100k): Conglomerate multiple revenue streams',
    'Lead as Strategist — oversee M&A, decentralized innovation, and company culture',
    'Capture video/written testimonials from satisfied clients',
    'Request Google reviews after positive feedback moments',
    'Enrol clients in formal referral rewards program',
    'Invite top clients to Customer Advisory Board',
    'Co-create content: webinars, blog posts, case studies',
    'Recognise advocates in newsletters and community channels',
  ],
};

export const STAGE_FALLBACK_SCRIPTS: Record<string, string[]> = {
  'Lead Conversion': [
    // BANT — Budget check
    `Hi {{name}}, before we go further, I want to make sure we're aligned. Our programs start at R197/mo. Is that in line with what you'd be comfortable investing to solve this?`,

    // BANT — Authority check
    `Just confirming, {{name}} — are you the main decision-maker for this, or would someone else need to be involved?`,

    // Hosting Handshake
    `{{name}}, while the free 1-pager is live at the Cloudflare link, a professional image needs a real domain. I can manage it for you — R197/year (.co.za) or R397/year (.com) — I handle everything. Want to lock yours in?`,

    // Discovery Call SPS (Specific Purpose Statement)
    `Hi {{name}}, the specific purpose of this 15-minute call is to understand your goals and your current challenges, see if we're a good fit, and if so, determine the next steps. Does [DAY] at [TIME] work for you?`,

    // Recommendation Pivot (after Discovery Call diagnosis)
    `Based on what you've told me about [PAIN] and your goal to [DREAM], I'm confident the Goal-to-10 BizInception Program is the best next step. It solves [PAIN] by getting you to 10 paying customers in 30 days. Want to see a 5-minute demo?`,

    // 4 P's framework — Picture
    `Imagine waking up every morning knowing your pipeline has new leads from referrals while your business builds itself. That's what we create together.`,

    // 4 P's framework — Promise
    `Here's our promise: we will get you to your first 10 paying customers within 30 days. If you follow the system, it works — the success math proves it.`,

    // 3-Touch — Touch 1 (48h after proposal, no response)
    `Hey {{name}}, just checking if you had any trouble with the payment link or if you had any final questions after the proposal. I'm here if you need me.`,

    // 3-Touch — Touch 2 (96h, calendar lock)
    `Hi {{name}}, I'm finalizing my schedule for next week. Would you still like to secure your spot for the Goal-to-10 program, or should I move your file to our Nurture list for a later date?`,

    // 3-Touch — Touch 3 (7 days, bless and release)
    `It seems now might not be the right time, and that's okay! I'm moving your details to my Nurture list. I'll check in with you in 90 days. Feel free to reach out if your situation changes sooner.`,

    // Discovery Call — protect your time (anti-free-coaching pivot)
    `That's exactly what we solve in the program. To give you the right answer, I first need to finish mapping out your Current State versus your Future State. Let's stay focused on that for now.`,

    // After BANT fail → Nurture
    `{{name}}, it sounds like now might not be the ideal time. No worries — I'll add you to my Nurture list. I'll follow up in 90 days with some resources that might help in the meantime.`,

    // Discovery Call — SPS (Specific Purpose Statement)
    `Hi {{name}}, the specific purpose of this 15-minute call is to understand your goals and your current challenges, see if we're a good fit, and if so, determine the next steps. Does [DAY] at [TIME] work for you?`,

    // Discovery Call — Health Check Question
    `Great, let's start with a quick health check. What's the ONE biggest challenge you're facing in your business right now? Knowing this helps me understand if we're the right fit before we dive deeper.`,

    // Discovery Call — Goal Clarification
    `I hear you. Let's make sure we're on the same page. You mentioned wanting to [GOAL] — is that still your top priority? What does success look like to you in the next 90 days?`,

    // Discovery Call — Current State Assessment
    `Before we talk about solutions, I want to understand your current situation. How are you currently generating leads? What's working, what isn't, and what's costing you the most in time or money?`,

    // Discovery Call — Budget Check (after needs identified)
    `Great, I can see you have [NUM] potential customers. Now, for the next 30 days, how much are you willing to invest to reach your first 10? This helps me tailor the right solution for your budget and timeline.`,

    // Discovery Call — Authority Validation
    `Perfect, you're ready to move forward. To make sure we're respecting your time, you mentioned you're the decision-maker — is that correct? If someone else needs to be involved, let me know and I can prepare accordingly.`,

    // Discovery Call — Timeline Confirmation
    `Excellent, I can see you're committed to taking action. What's your ideal timeline for launching? Having a clear deadline helps us build momentum and accountability.`,

    // Discovery Call — Move to Proposal
    `Based on what we've covered, I believe the Goal-to-10 BizInception Program is the right fit. I'll send you a 1-page proposal within 24 hours. Does that work for you?`,

    // Discovery Call — Anti-Free-Coaching Pivot
    `I understand you want to save money, and that's smart. However, to give you the best advice, I need to finish mapping out your Current State vs Future State first. This only takes 15 minutes. Does [TIME] work for our call?`,

    // Discovery Call — Next Steps Closing
    `I'm excited about our potential. Here's what happens next: (1) I send the proposal; (2) you review it; (3) we schedule the demo. Sound good to you?`,

    // Discovery Call — Schedule Follow-up if Not Ready
    `No problem at all. Let's revisit this in 30 days. I'll send you a quick check-in then. Until then, feel free to reach out if you have questions. Have a great day!`,
  ],

  'Fulfillment': [
    // Golden Handshake — Welcome Pack
    `🎉 Welcome to the BizBuilder family, {{name}}! Your command center is ready. Check your inbox for login details to your BizBuilder App, your private client folder, and your Activation Tasks. Let's get you to 10 customers!`,

    // Activation Task 1 — Lesson 1
    `Hi {{name}}, your first task: complete Lesson 1 (Platform Orientation) in the app. It takes about 10 minutes and shows you how everything works. Let me know once you're done!`,

    // Activation Task 2 — Freedom Number
    `{{name}}, next up: set your Freedom Number in the Goals section. This is the exact monthly revenue you need to fund your ideal lifestyle. Your entire journey is built around reaching this number.`,

    // Activation Task 3 — Hotlist 100
    `Hey {{name}}, your third task: start your Hotlist 100 in Marketing. List everyone you know who could be a potential customer or referral partner. This is your goldmine — we'll work through it systematically.`,

    // Stage 1 progress check-in
    `Hi {{name}}, how's the Inception Sprint going? I can see you've completed [X] lessons. Remember, the 10-a-Day Mantra: have 10 sales conversations every day. The 15:5:3 ratio proves it works — 15 outreaches → 5 conversations → 3 sales.`,

    // Coaching check-in (for coaching program clients)
    `{{name}}, quick check-in before our weekly consultation. What's your biggest win this week? What's the #1 thing blocking you? Bring both to our call so we can celebrate the win and crush the blocker.`,

    // Drip lesson unlocked notification
    `Great work {{name}}! I can see you completed your tasks. Your next playbook lesson has been unlocked in the app. Remember: log in the App, archive in the Sheet.`,

    // 10-Customer trigger approaching
    `{{name}}, you're at [X] verified customers — almost at the Goal-to-10 milestone! Once you hit 10, I'll show you the Goal-to-100 Launch program. Keep pushing — you're almost there!`,

    // Graduation — Stage 1 complete
    `🚀 CONGRATULATIONS {{name}}! You've hit 10 verified paying customers! This is a MASSIVE milestone. Your Stage 1 is complete. Now let's talk about scaling to 100 with the Goal-to-100 Full-Scale Launch Program. You've proven the model works — now let's pour fuel on it.`,

    // Satisfaction Survey — 7-Day Health Check
    `Hi {{name}}, quick one-question check-in: On a scale of 1 to 5, how happy are you with the progress we've made in your first week? Your honest answer helps me serve you better.`,
  ],

  'Retention': [
    // Golden Moment — testimonial + referral (score 4+)
    `{{name}}, I'm thrilled you're happy with the progress! Two quick favours: (1) Would you write a short testimonial about your experience? (2) Do you know 1-2 business owners who could benefit from what we've done together? Warm introductions are always appreciated!`,

    // Monthly account review
    `Hi {{name}}, time for our monthly review. Here's what we delivered this month: [OUTCOMES]. Here's what's coming next: [NEXT]. Let's schedule a 15-min call to review.`,

    // Engagement dip alert
    `Hey {{name}}, I noticed you haven't logged into the app in [X days]. Everything okay? Sometimes life gets busy — let me know if there's anything I can help unblock. Remember, consistency is the secret to hitting your Freedom Number.`,

    // Goal-to-100 upsell presentation
    `{{name}}, you've mastered Stage 1. Now it's time to scale. The Goal-to-100 Full-Scale Launch Program takes you from 10 to 100 customers using the "Power of One" strategy — one ideal customer persona, one reliable demand channel. Interested in a quick overview?`,

    // Franchise Flip (for referral partners ready to coach)
    `{{name}}, with your results, you're qualified for the Franchise Flip. Instead of earning 20% commission on referrals, you become a Goal-to-10 Coach yourself and keep 80% of the revenue. This turns your business from a side-hustle into an enterprise. Want to explore it?`,

    // NPS/CSAT collection
    `Hi {{name}}, quick feedback request: How likely are you to recommend BizBuilder to another entrepreneur? (1-10). Your honest input shapes how we serve you better.`,
  ],

  'Growth': [
    // Upsell introduction
    `{{name}}, your results speak for themselves — [X] customers, [X] revenue. I think you're ready for our premium tier. It adds [FEATURE] which typically increases CLV by 40%. Want to see how it fits into your current setup?`,

    // Referral Machine activation
    `{{name}}, the math is simple: every satisfied customer refers 3 more, those 3 refer 3 each = 9, then 27. At your current satisfaction rate, we can double your customer base through referrals alone. Let me show you how the Referral Machine works.`,

    // Leverage strategy session
    `Hi {{name}}, let's talk scaling. Which leverage would give you the biggest ROI right now — Labor (hiring your first team member), Capital (investing in ads), Content (creating a course), or Code (building a tool)? Happy to whiteboard this with you.`,

    // SOP documentation prompt
    `{{name}}, as you scale, you need to document everything. Start with your top 3 repeatable processes. Write them as SOPs. This is how you build a business that runs without you — the first step to the 10/200 Leadership Ratio.`,

    // Success story feature request
    `{{name}}, your growth story is incredible. We'd love to feature it as a case study. It inspires other entrepreneurs and positions you as an Industry Insider in your market. Interested? It's great free exposure.`,
  ],

  'Advocacy': [
    // Google review request
    `Hi {{name}}, would you take 30 seconds to share your experience on Google? Here's the direct link: [REVIEW_LINK]. Your review helps other entrepreneurs find us — and it helps me personally. Every review is a gift! 🌟`,

    // Customer Advisory Board invitation
    `{{name}}, I'm forming a small group of our most successful clients to help shape the future of BizBuilder. You'd get early access to new features, input on our roadmap, and a direct line to me. Interest in joining our Customer Advisory Board?`,

    // Co-creation opportunity
    `Hey {{name}}, how about we co-create a webinar together about [TOPIC]? Your story would inspire so many entrepreneurs still in the trenches. I handle the tech and promotion — you just show up and share your journey. You in?`,

    // Referral program enrolment
    `{{name}}, I've set up your formal referral program link. Here's how it works: for every paying client you refer, you earn 20% recurring commission. It's our way of saying thank you for helping us grow. Your unique link: [REFERRAL_LINK]`,

    // Newsletter feature
    `{{name}}, we'd love to feature your success in this month's newsletter going out to [X] subscribers. Great for your brand and your business. Just need a photo and a 2-sentence quote. Can I include you?`,
  ],
};

// ─── Legacy Stage Mapping ────────────────────────────────────────────────────

/**
 * Maps legacy 8-stage WP4/WP5 journey names to the current 5-Phase CJMS stages.
 * Lead Capture/Qualification/Needs Assessment/Product Selection → Lead Conversion
 * Implementation/Launch → Fulfillment
 * Growth → Retention
 * Advocacy → Advocacy
 */
export const LEGACY_CUSTOMER_STAGE_MAP: Record<string, string> = {
  'Lead Capture': 'Lead Conversion',
  'Qualification': 'Lead Conversion',
  'Needs Assessment': 'Lead Conversion',
  'Product Selection': 'Lead Conversion',
  'Implementation': 'Fulfillment',
  'Launch': 'Fulfillment',
  'Growth': 'Retention',
  'Advocacy': 'Advocacy',
};

/**
 * All known journey stages (for analytics completeness).
 * First 2 cover the Lead side (Lead Generation → Lead Conversion transition),
 * the remaining 4 cover the full Customer lifecycle from onboarding to advocacy.
 */
export const ALL_JOURNEY_STAGES: string[] = [
  'Lead Generation',    // Phase 1: Awareness & Acquisition
  'Lead Conversion',     // Phase 2+3: Engagement → Conversion
  'Fulfillment',          // Phase 3: Onboarding + Stage 1 Inception Sprint
  'Retention',            // Phase 4: Launch (Goal-to-100) + Known Brand
  'Growth',               // Phase 4: Goal-to-1k — Client Monetization
  'Advocacy',             // Phase 4+5: Referral Machine → Freedom & Expansion
];

// ─── Rich Stage Content (the full CJMS framework per stage) ──────────────────

/**
 * Phase 1: Awareness & Acquisition (Stranger → Lead)
 *
 * The objective is to identify a "starving crowd" experiencing a "Rainy Day" pain point
 * and capture their interest through the Rule of 100 and the Irresistible Bribe.
 */
const LEAD_STAGE_CONTENT: Record<string, StageContent> = {
  'Lead Generation': {
    displayName: 'Phase 1 — Awareness & Acquisition',
    phase: 1,
    description:
      'Move strangers into your world as leads using the Rule of 100 daily outreach and the Irresistible Bribe (FREE 1-Pager Website in exchange for 5 business referrals). Every interaction must be logged in Marketing_Exec_Data or CRM_Leads. The 15:5:3 ratio governs success: 15 outreaches → 5 conversations → 3 sales.',
    steps: [
      'Define Your Freedom Number — Log the exact monthly revenue needed for your ideal lifestyle in Exec_Strat_Goals_Data.',
      'Establish the 30-Day Inception Goal — Your singular mission: secure 10 paid members to prove your concept.',
      'Identify Your "Starving Crowd" — Find prospects with PAIN (unprofessional web presence), POWER (decision-maker), ACCESSIBILITY (reachable via WhatsApp), and TIMING (need it now).',
      'Execute the Rule of 100 — Perform 100 primary actions daily: outreach messages to Hotlist 100 + content posts in communities.',
      'Deploy the Irresistible Bribe — Offer a professional 1-Pager Website (worth R3,997) for free in exchange for 5 business referrals.',
      'Verify Referrals & Deliver — Once 5 referrals accept the offer, deliver the free 1-pager. Then pivot to the Hosting Handshake (R197/year managed domain).',
    ],
    activities: [
      'Spend focused outreach minutes in digital communities messaging your Hotlist 100',
      'Post "before and after" 1-pager website examples to demonstrate Solution Expertise',
      'Send Irresistible Bribe script to every new contact — track response rate',
      'Log all leads as "Leads Generated" in Marketing_Exec_Data or CRM_Leads tab',
      'Verify referral submissions — track who the lead referred and their status',
      'Use Exists Verification — the system checks your data tabs to prove outreach is happening',
      'Follow up with leads stuck below 5 referrals — offer help contacting their referrals',
    ],
    tasks: [
      { label: 'Perform 100 outreach actions today', detail: 'Apply the Rule of 100: messages, posts, comments. Your pipeline depends on volume — the 15:5:3 ratio proves it.' },
      { label: 'Send Irresistible Bribe script', detail: 'Send the free 1-pager website offer with the 5-referral condition. Script: "I\'m running a mission to help 100 local businesses get online..."' },
      { label: 'Log leads in CRM_Leads', detail: 'Log every lead as "Leads Generated" in the CRM_Leads (Hub sheet) or Marketing_Exec_Data (Client sheet) tab. No lead is lost in chat history.' },
      { label: 'Track referral progress', detail: 'Use the referral tracking page to monitor how many referrals each lead has submitted. Follow up with leads who haven\'t hit 5 yet.' },
      { label: 'Deliver free 1-pager website', detail: 'Once a lead reaches 5 referral acceptances, build and deploy their 1-pager via the Website Builder + Cloudflare Pages.' },
      { label: 'Transition to Hosting Handshake', detail: 'After delivering the free site, offer the R197/yr .co.za or R397/yr .com managed domain hosting. This is the Tripwire that converts a free lead into a paying customer.' },
    ],
    scripts: LEAD_FALLBACK_SCRIPTS['Lead Generation'] || [],
    resources: [
          { title: 'Hotlist 100 Template', description: 'Template for listing your 100 warm contacts to target first', type: 'template' },
          { title: 'Irresistible Bribe Script', description: 'The exact script for offering the free 1-pager with the referral mechanism', type: 'template' },
          { title: '15:5:3 Ratio Tracker', description: 'Track your outreach → conversation → sale conversion rates', type: 'checklist' },
          { title: 'Referral Tracking Dashboard', description: 'Monitor referral submissions and status per lead', type: 'tool' },
          { title: 'Phase 1: Awareness & Acquisition Guide', description: 'Full execution guide for identifying starving crowds and deploying the Rule of 100', type: 'guide' },
        ],
    stepActivityMap: [
      [0, 1], // Define Your Freedom Number → activities 0,1
      [2],    // Establish your 30-Day Inception Goal → activity 2
      [3, 4], // Identify Your Starving Crowd → activities 3,4
      [5, 6], // Execute the Rule of 100 → activities 5,6
      [],     // Deploy the Irresistible Bribe
      [],     // Verify Referrals & Deliver
    ],
  },
};

/**
 * Phase 2+3: Engagement & Qualification → Conversion & Onboarding
 *
 * Phase 2 (Engagement & Qualification): Move Lead → Prospect through BANT check,
 * Hosting Handshake, Discovery Call, and qualification. Protect your time — only
 * invest in high-probability opportunities.
 *
 * Phase 3 (Conversion & Onboarding): Move Prospect → Customer through 4 P\'s demo,
 * 1-Page Proposal, 3-Touch payment follow-up, Provisioning Engine, and Stage 1 sprint.
 */
const CUSTOMER_STAGE_CONTENT: Record<string, StageContent> = {
  'Lead Conversion': {
    displayName: 'Phase 2+3 — Engagement & Conversion',
    phase: 2,
    description:
      'Phase 2 (Engagement): Triage leads through BANT qualification (Budget, Authority, Need, Timeline). Deploy the Hosting Handshake (R197 Tripwire). Conduct a 15-min Discovery Call as a "Business Doctor" — diagnose, don\'t coach for free.\\n\\nPhase 3 (Conversion): Present using the 4 P\'s Framework (Picture, Promise, Proof, Push). Send a 1-Page Proposal. Execute the 3-Touch Rule for payment follow-up (48h → 96h → 7d). Close with the Golden Handshake Welcome Pack.',
    steps: [
      // Phase 2: Engagement & Qualification
      'BANT Qualification — Verify Budget (R197+/mo), Authority (decision-maker), Need (1-10 urgency), Timeline (30-day readiness).',
      'Hosting Handshake (Tripwire) — Offer managed Cloudflare domain hosting at R197/yr (.co.za) or R397/yr (.com). First financial commitment. Provision the domain.',
      '15-Min Discovery Call — Open with Specific Purpose Statement (SPS). Map Current State (Pain) vs Future State (Dream). Listen 80%, dig into consequences. Protect your time — pivot if they seek free coaching.',
      'Recommendation Pivot — Once diagnosed: "Based on what you told me about [PAIN], I\'m confident [PROGRAM] is the best next step. It solves [PAIN] by [BENEFIT].\" Book the demo on the call.',
      // Phase 3: Conversion & Onboarding
      '4 P\'s Demo — Start with the "Killer Feature" addressing their biggest pain. Show Picture (future state), Promise (specific outcome), Proof (testimonials/case studies), Push (urgency + clear next step).',
      '1-Page Proposal — Deliver a single page containing: (1) The Goal, (2) The Problem, (3) The Solution, (4) The Investment, (5) The Next Step. Include risk reversal (guarantee).',
      '3-Touch Payment Follow-Up — Touch 1 (48h): Helpful check-in. Touch 2 (96h): Calendar lock. Touch 3 (7d): Bless and release to Nurture list.',
      'Close & Provision — On payment verification, trigger Provisioning Engine: clone client Sheet, create GitHub folder, generate Magic Link. Send Golden Handshake Welcome Pack with 3 Activation Tasks.',
    ],
    activities: [
      'Run BANT check within first meaningful conversation — don\'t sell before qualifying',
      'Send Hosting Handshake offer immediately after free 1-pager delivery',
      'Discovery Call: Set the frame with SPS, maintain 80/20 listen/speak ratio',
      'Use the anti-free-coaching pivot: "That\'s exactly what we solve in the program"',
      'Log qualified leads as "Qualified (BANT)" in Sales_Exec_Data',
      'Move BANT-fail leads to Nurture List — they are "Not Right Nows", not lost',
      'Present the 4 P\'s Demo starting with the "Bang" Killer Feature',
      'Send 1-Page Proposal within 2 hours of demo — eliminate Interest Decay',
      'Execute 3-Touch Rule: log each touch, escalate urgency gradually',
      'On payment: run Provisioning Engine, send Welcome Pack, verify 3 Activation Tasks',
      'Update CRM status to "Closed - Won" in Sales_Exec_Data',
    ],
    stepActivityMap: [
      [0, 4, 5],
      [1],
      [2, 3],
      [],
      [6, 7],
      [],
      [8],
      [9, 10],
    ],
    tasks: [
      { label: 'Run BANT qualification', detail: 'Ask: Budget (\"R197+/mo in line?\"), Authority (\"You the decision-maker?\"), Need (\"1-10, how critical?\"), Timeline (\"When would you start?\").' },
      { label: 'Execute Hosting Handshake', detail: 'Offer R197/yr managed domain hosting. Transition from "free lead" to "paying customer." Cover the technical setup entirely.' },
      { label: 'Conduct Discovery Call (SPS)', detail: 'Open: "The specific purpose of this 15-minute call is to understand your goals, see if we\'re a fit, and determine next steps." Map Pain vs Dream.' },
      { label: 'Present 4 P\'s Demo', detail: 'Picture (future outcome), Promise (specific commitment), Proof (testimonials), Push (urgency + call to action). Start with the Killer Feature.' },
      { label: 'Send 1-Page Proposal', detail: '5 required elements: Goal, Problem, Solution, Investment, Next Step. Include risk reversal. Keep it one page — no Interest Decay.' },
      { label: 'Execute 3-Touch Rule', detail: 'Touch 1 (48h): "Any trouble with payment link?" Touch 2 (96h): "Should I secure your spot?" Touch 3 (7d): "Moving you to Nurture — I\'ll check in 90 days."' },
      { label: 'Send Golden Handshake Welcome Pack', detail: 'App login, client folder link, 3 Activation Tasks: (1) Lesson 1, (2) Set Freedom Number, (3) Start Hotlist 100.' },
    ],
    scripts: STAGE_FALLBACK_SCRIPTS['Lead Conversion'] || [],
    resources: [
      { title: 'BANT Qualification Script', description: 'Budget/Authority/Need/Timeline questions with exact phrasing', type: 'template' },
      { title: 'Discovery Call SPS & Pivot Scripts', description: 'Specific Purpose Statement + anti-free-coaching pivots', type: 'template' },
      { title: '4 P\'s Framework Template', description: 'Picture/Promise/Proof/Push structure for demos and proposals', type: 'template' },
      { title: '1-Page Proposal Template', description: '5-element single-page proposal with risk reversal included', type: 'template' },
      { title: '3-Touch Rule Playbook', description: 'Full payment follow-up sequence with exact scripts for each touch', type: 'guide' },
      { title: 'Hosting Handshake Script', description: 'Transition from free site to managed hosting — exact pivot wording', type: 'template' },
      { title: 'Golden Handshake Welcome Pack', description: 'Email template with Activation Tasks, login deeplink, and client folder link', type: 'template' },
    ],
  },

  /**
   * Phase 3: Fulfillment — Onboarding + Stage 1 (Inception) Sprint
   *
   * Once the customer is provisioned, the 14-30 day Stage 1 sprint begins.
   * Content is dripped on verified task completion (Anti-Cheating mechanism).
   * Goal: 10 verified paying customers to graduate to Stage 2.
   */
  'Fulfillment': {
    displayName: 'Phase 3 — Onboarding & Inception Sprint',
    phase: 3,
    description:
      'The Stage 1 (Inception) Sprint: a 14-30 day phase to secure 10 paying customers and prove the concept. The system drips content on verified task completion via the Sync Gate (Anti-Cheating mechanism). For coaching clients: weekly consultations. The 10-Customer Trigger — verified in OF_Exec_Data — unlocks Stage 2 (Goal-to-100 Launch).',
    steps: [
      'Provisioning Engine — System actions in <10 seconds: clone Client Sheet, create GitHub folder, generate Magic Link, stamp UserID.',
      'Golden Handshake — Send Welcome Pack with 3 Activation Tasks: (1) Lesson 1 Platform Orientation, (2) Set Freedom Number, (3) Start Hotlist 100.',
      'Content Dripping — Each time the client completes a playbook lesson and its task(s), the Sync Gate verifies work exists in data tabs before dripping the next lesson.',
      '10-a-Day Mantra — Client must have 10 sales conversations daily. Log all in the Cockpit.',
      'Weekly Coaching — For coaching program clients: review wins, unblock obstacles, track toward 10.',
      'Graduation — When 10 paying customers are verified in OF_Exec_Data, trigger Goal-to-100 Launch upsell.',
    ],
    activities: [
      'On payment verification: fire Provisioning Engine — clone Sheet, create GitHub folder, generate Magic Link',
      'Send Welcome Pack email within 5 minutes of provisioning — instant access = momentum',
      'Monitor content drip: verify task completion before dripping next lesson',
      'Log every client interaction in the Cockpit for Performance Tracking (Law 6)',
      'Run weekly coaching consultations for coaching program clients',
      'Track 10-Customer Trigger: count verified paying customers in OF_Exec_Data',
      'Nudge stuck clients — send progress reminders, offer help unblocking',
      'When 10 customers verified: send Satisfaction Survey, then Goal-to-100 upsell',
    ],
    stepActivityMap: [
      [0],
      [1],
      [2, 3],
      [],
      [4, 5, 6],
      [7],
    ],
    tasks: [
      { label: 'Verify Provisioning Engine fired', detail: 'Confirm Client Sheet cloned, GitHub folder created, Magic Link generated, UserID stamped in settings.' },
      { label: 'Send Golden Handshake Welcome Pack', detail: '3 Activation Tasks: (1) Complete Lesson 1, (2) Set Freedom Number in Goals, (3) Start Hotlist 100 in Marketing.' },
      { label: 'Monitor 10-a-Day adherence', detail: 'Check daily that client is logging 10 sales conversations. Remind them: the 15:5:3 ratio governs success.' },
      { label: 'Drip next playbook lesson', detail: 'Verify task completion via Sync Gate (Anti-Cheating). If verified, drip the next lesson and its associated tasks.' },
      { label: 'Conduct weekly coaching session', detail: 'For coaching clients: review wins, unblock obstacles, track customer count, provide guidance. Log session notes.' },
      { label: 'Verify 10-Customer graduation trigger', detail: 'Count verified paying customers in OF_Exec_Data. At 10: trigger Satisfaction Survey + Goal-to-100 Launch upsell.' },
    ],
    scripts: STAGE_FALLBACK_SCRIPTS['Fulfillment'] || [],
    resources: [
      { title: 'Stage 1 Inception Sprint Plan', description: '14-30 day goal-to-10 execution roadmap with milestones', type: 'guide' },
      { title: 'Welcome Pack Email Template', description: 'Golden Handshake with Activation Tasks, login, and folder links', type: 'template' },
      { title: 'Content Drip Trigger Checklist', description: 'Verify task completion before dripping next lesson — Anti-Cheating', type: 'checklist' },
      { title: 'Weekly Coaching Session Template', description: 'Win/unblock/track format for coaching consultations', type: 'template' },
    ],
  },

  /**
   * Phase 4: Retention — Launch (Goal-to-100) + Known Brand
   *
   * Customer has graduated Stage 1 (10 customers). Now scale to 100 using
   * the "Power of One" strategy. The Referral Machine powers exponential growth:
   * 7-Day Health Check → Satisfaction Survey → (4+ score) → Golden Moment:
   * testimonial + referral → pipeline refill.
   */
  'Retention': {
    displayName: 'Phase 4 — Launch & Referral Machine',
    phase: 4,
    description:
      'The Stage 2 (Launch) phase: scale from 10 to 100 customers. Apply the "Power of One" — one ideal customer persona, one reliable demand channel. Deploy the 7-Day Health Check satisfaction survey. Scores of 4+ trigger the Golden Moment: capture a testimonial AND ask for 1-2 referrals. These warm introductions feed the Referral Machine back into your pipeline, enabling exponential growth (1→3→9→27). Path A clients qualify for the Franchise Flip (20% → 80% revenue).',
    steps: [
      '7-Day Health Check — Send one-question survey: "On a scale of 1-5, how happy are you with Week 1 progress?"',
      'Golden Moment (score 4+) — Immediately: capture testimonial + ask for 1-2 referrals. Feed into Referral Machine.',
      'Power of One Strategy — Stabilize growth: one ideal customer persona, one reliable demand channel.',
      'Monthly Account Review — Show ROI, outcomes delivered, upcoming milestones. Track engagement metrics.',
      'Whole Product Development — Move from selling a feature to a complete, high-value solution that increases ROI.',
      'Referral Machine Loop — Satisfied customer → testimonial → referrals → new leads → satisfied customers (exponential).',
      'Goal-to-100 Graduation — At 100 customers, unlock Stage 3 (Goal-to-1k Known Brand) and Franchise Flip offer.',
    ],
    activities: [
      'Run 7-Day Health Check on every customer exactly 7 days post-onboarding',
      'Golden Moment: for 4+ scores — immediately ask for testimonial + 1-2 referrals',
    ],
    stepActivityMap: [
      [0],
      [1],
      [],
      [],
      [],
      [],
      [],
    ],
    tasks: [
      { label: 'Run 7-Day Health Check', detail: 'Send survey exactly 7 days after onboarding: "On a scale of 1-5, how happy are you with Week 1 progress?"' },
      { label: 'Process Golden Moment', detail: 'For scores 4+: capture testimonial + ask for 1-2 referrals. Log testimonial, add referrals to Hotlist 100.' },
      { label: 'Conduct Monthly Account Review', detail: 'Show ROI, outcomes, upcoming milestones. Track engagement metrics in Cockpit.' },
      { label: 'Implement Power of One Strategy', detail: 'Define ideal customer persona and reliable demand channel. Align all marketing/sales efforts.' },
      { label: 'Execute Referral Machine Loop', detail: 'Track testimonial → referrals → new leads → conversion. Automate follow-up sequence.' },
      { label: 'Verify Goal-to-100 Graduation', detail: 'At 100 customers: trigger Franchise Flip offer + unlock Stage 3 (Goal-to-1k Known Brand).' },
    ],
    scripts: STAGE_FALLBACK_SCRIPTS['Retention'] || [],
    resources: [
      { title: 'Stage 2 Launch Plan', description: 'Execution roadmap for scaling from 10 to 100 customers', type: 'guide' },
      { title: '7-Day Health Check Survey Template', description: 'One-question satisfaction survey with scoring guide', type: 'template' },
      { title: 'Golden Moment Script Template', description: 'Exact wording for testimonial + referral request (4+ scores)', type: 'template' },
      { title: 'Power of One Strategy Guide', description: 'How to identify and lock in your ideal customer persona and demand channel', type: 'guide' },
      { title: 'Referral Machine Playbook', description: 'Full system for turning testimonials into referrals into new customers', type: 'guide' },
    ],
  },

  /**
   * Phase 4: Growth — Goal-to-1k — Client Monetization
   *
   * Scale from 100 to 1,000 customers. Focus on leverage, automation, and
   * increasing Customer Lifetime Value through upsells/cross-sells.
   */
  'Growth': {
    displayName: 'Phase 4 — Growth & Monetization',
    phase: 4,
    description:
      'The Stage 3 (Growth) phase: scale from 100 to 1,000 customers. Apply leverage: Labor (hire team), Capital (ads), Content (create courses), Code (build tools). Develop upsell/cross-sell strategy for CLV maximization. Segment market for higher efficiency targeting. Target 10/200 Leadership Ratio — you lead, team executes.',
    steps: [
      'Develop upsell/cross-sell strategy for CLV maximization',
      'Apply leverage: Labor (hire team), Capital (ads), Content, Code',
      'Master the "Power of One" — one persona, one channel',
      'Document SOPs for repeatable delivery process',
      'Segment market for higher efficiency targeting',
      'Target 10/200 Leadership Ratio — you lead, team executes',
    ],
    activities: [
      'Create upsell/cross-sell packages for existing customers',
      'Implement leverage strategy (hire first team member, launch ad campaign)',
      'Document top 3 repeatable processes as SOPs',
      'Run market segmentation analysis and create targeted campaigns',
      'Track leadership ratio and delegate operational tasks',
    ],
    stepActivityMap: [
      [0],
      [1],
      [],
      [2],
      [3],
      [4],
    ],
    tasks: [
      { label: 'Develop Upsell/Cross-sell Strategy', detail: 'Analyze customer journey for natural upgrade/complementary offers. Create pricing and delivery structure.' },
      { label: 'Implement Labor Leverage', detail: 'Hire first team member to handle operational tasks. Define role, responsibilities, and onboarding process.' },
      { label: 'Implement Capital Leverage', detail: 'Launch targeted ad campaign to acquire customers at scale. Monitor ROI and optimize.' },
      { label: 'Implement Content Leverage', detail: 'Create digital course or membership site. Structure content for progressive learning.' },
      { label: 'Implement Code Leverage', detail: 'Build tool or automation to solve recurring problems. Package as reusable solution.' },
      { label: 'Execute Market Segmentation', detail: 'Analyze customer base for distinct segments. Create targeted messaging and offers for each.' },
      { label: 'Achieve 10/200 Leadership Ratio', detail: 'Delegate operational tasks to team. Focus 10% on strategy, 200% on execution through team.' },
    ],
    scripts: STAGE_FALLBACK_SCRIPTS['Growth'] || [],
    resources: [
      { title: 'Stage 3 Growth Plan', description: 'Execution roadmap for scaling from 100 to 1,000 customers', type: 'guide' },
      { title: 'Upsell/Cross-sell Strategy Template', description: 'Framework for creating profitable upgrade paths', type: 'template' },
      { title: 'Leverage Implementation Guide', description: 'Step-by-step for hiring, ads, content creation, and tool building', type: 'guide' },
      { title: 'Market Segmentation Workbook', description: 'Tools for identifying and targeting high-value customer segments', type: 'tool' },
      { title: 'Leadership Ratio Tracker', description: 'Monitor time allocation: strategy vs execution through team', type: 'checklist' },
    ],
  },

  /**
   * Phase 5: Freedom & Expansion — Goal-to-10k → Goal-to-100k
   *
   * Scale from 1,000 to 10,000+ customers. Focus on systems, automation,
   * and building a business that runs without you.
   */
  'Advocacy': {
    displayName: 'Phase 5 — Freedom & Expansion',
    phase: 5,
    description:
      'The Stage 4 & 5 (Freedom & Expansion) phase: scale from 1,000 to 10,000+ customers. Achieve 10/200 Leadership Ratio — 10h strategic guidance vs 200h team execution. Focus on intensive market segmentation and professionalized departments. Build conglomerate of multiple revenue streams. Lead as Strategist — oversee M&A, decentralized innovation, and company culture.',
    steps: [
      'Stage 4 — Full Operation (Goal-to-10k): Hire management team to run repeatable model',
      'Achieve 10/200 Leadership Ratio — 10h strategic guidance vs 200h team execution',
      'Focus on intensive market segmentation and professionalized departments',
      'Stage 5 — Corporate (Goal-to-100k): Conglomerate multiple revenue streams',
      'Lead as Strategist — oversee M&A, decentralized innovation, and company culture',
      'Capture video/written testimonials from satisfied clients',
      'Request Google reviews after positive feedback moments',
      'Enrol clients in formal referral rewards program',
      'Invite top clients to Customer Advisory Board',
      'Co-create content: webinars, blog posts, case studies',
      'Recognise advocates in newsletters and community channels',
    ],
    activities: [
      'Build management team and delegate operational responsibilities',
      'Implement advanced market segmentation and targeting systems',
      'Develop and launch additional revenue streams/products',
      'Establish M&A criteria and pipeline for strategic acquisitions',
      'Create innovation budget and process for testing new ideas',
      'Produce and distribute thought leadership content',
      'Monitor and nurture advocate relationships through exclusive benefits',
    ],
    stepActivityMap: [
      [0],
      [],
      [1],
      [2],
      [3, 4],
      [],
      [],
      [],
      [6],
      [5],
      [],
    ],
    tasks: [
      { label: 'Build Management Team', detail: 'Hire operations, marketing, and finance leads. Define clear roles, responsibilities, and KPIs.' },
      { label: 'Implement Advanced Market Segmentation', detail: 'Use RFM analysis and clustering to identify high-value segments. Create tailored offerings.' },
      { label: 'Develop Additional Revenue Streams', detail: 'Research market gaps. Create minimum viable products. Test with existing customer base.' },
      { label: 'Establish M&A Framework', detail: 'Define acquisition criteria, valuation methods, and integration process. Build deal pipeline.' },
      { label: 'Create Innovation Process', detail: 'Allocate budget and time for experimentation. Implement rapid prototyping and testing cycle.' },
      { label: 'Produce Thought Leadership Content', detail: 'Create webinars, blog posts, videos, and podcasts. Distribute through owned and earned channels.' },
      { label: 'Nurture Advocate Relationships', detail: 'Create exclusive benefits program. Host quarterly gatherings. Publicly recognize top advocates.' },
    ],
    scripts: STAGE_FALLBACK_SCRIPTS['Advocacy'] || [],
    resources: [
      { title: 'Stage 4&5 Expansion Plan', description: 'Execution roadmap for scaling from 1,000 to 10,000+ customers', type: 'guide' },
      { title: 'Management Team Structure Template', description: 'Roles, responsibilities, and KPIs for scaling leadership', type: 'template' },
      { title: 'Advanced Segmentation Playbook', description: 'Methods for identifying and targeting profitable customer segments', type: 'guide' },
      { title: 'M&A Framework Guide', description: 'Step-by-step for evaluating, negotiating, and integrating acquisitions', type: 'guide' },
      { title: 'Innovation Process Manual', description: 'How to systematically test, learn, and scale new ideas within the organization', type: 'guide' },
    ],
  },
};

// Combine all stage content
export const STAGE_CONTENT: Record<string, StageContent> = {
  ...LEAD_STAGE_CONTENT,
  ...CUSTOMER_STAGE_CONTENT,
};

 /**
  * Get stage content for a given stage and lead/customer type
  * @param stage The stage name (e.g., 'Lead Generation', 'Fulfillment')
  * @param isLead Whether the client is a lead (true) or customer (false)
  * @returns The stage content object
  */
 export function getStageContent(stage: string, isLead: boolean): StageContent {
   // For leads, only Lead Generation stage exists
   if (isLead) {
     return LEAD_STAGE_CONTENT['Lead Generation'] || STAGE_CONTENT['Lead Generation'];
   }
   
   // For customers, return the appropriate stage content
   return STAGE_CONTENT[stage] || STAGE_CONTENT['Lead Conversion']; // fallback
 }

 /**
  * Get referral script template by day
  * @param day The day number (1, 3, 7, 10, 12, 14)
  * @returns Array of templates for that day
  */
 export function getReferralScriptTemplate(day: number): any[] {
   const templates = REFERRAL_SCRIPT_TEMPLATES[`day_${day}`];
   return templates || [];
 }

 /**
  * Personalize referral script template with placeholders
  * @param template The template string with placeholders
  * @param replacements Object with placeholder replacements
  * @returns Personalized template string
  */
 export function personalizeReferralScript(template: string, replacements: Record<string, string>): string {
   let personalized = template;
   for (const [key, value] of Object.entries(replacements)) {
     personalized = personalized.replace(`{${key}}`, value);
   }
   return personalized;
 }